import courseHero from '#/assets/courses/course-hero.webp'
import courseImg from '#/assets/courses/course-3.webp'

// ─── Hero ────────────────────────────────────────────────────────────────────
export const COURSE_HERO = {
  tag: 'Tajweed',
  title: 'Online Tajweed Rules Course',
  description:
    'Master the art of Quran recitation with proper Tajweed rules. This comprehensive course is designed for all levels, taught by certified scholars with years of experience in Quranic education.',
  heroImage: courseHero,
  courseImg: courseImg,
  // Standard course-level fields — all from backend
  level: 'All Levels',
  language: 'Arabic & English',
  category: 'Quranic Studies',
  badges: [
    'Live One-on-One Sessions',
    'Certified Ijazah Teachers',
    'Flexible Schedule',
  ],
}

// ─── About ───────────────────────────────────────────────────────────────────
export const COURSE_ABOUT = {
  eyebrow: 'ABOUT THE COURSE',
  title: 'Learn Tajweed the',
  highlight: 'Right Way',
  courseImg: courseImg,
  paragraphs: [
    'This course provides a structured and in-depth exploration of Tajweed rules — the science of proper Quranic recitation. Whether you are a beginner starting from scratch or an intermediate student looking to refine your recitation, our expert-led modules guide you step by step.',
    'Through live sessions and one-on-one feedback, you will gain practical mastery over every rule — from the points of articulation (Makharij Al-Huroof) to the rules of Noon Saakin, Meem Saakin, Madd, Waqf, and beyond.',
  ],
  highlights: [
    {
      title: 'Ijazah-Certified Instructors',
      description:
        'All instructors hold Ijazah — a chain of authority tracing back to the Prophet ﷺ, ensuring authentic transmission of Quranic knowledge.',
    },
    {
      title: 'Structured Learning Path',
      description:
        'Follow a carefully crafted curriculum with clear milestones and personalized feedback from your assigned teacher.',
    },
  ],
}

// ─── Key Features — icon-free (backend-ready) ────────────────────────────────
export const COURSE_KEY_FEATURES = [
  {
    title: 'Ijazah-Certified Instructors',
    description:
      'Learn from scholars who carry an unbroken chain of Quranic transmission from teacher to student since the time of the Prophet ﷺ.',
  },
  {
    title: 'Flexible Schedule',
    description:
      'Choose session times that fit your lifestyle. Morning, afternoon, or evening slots available 7 days a week.',
  },
  {
    title: 'Live & Recorded Sessions',
    description:
      'Attend interactive live classes and revisit recordings anytime. Every session is available on-demand for review.',
  },
  {
    title: 'Global Community',
    description:
      'Join thousands of students from over 80 countries learning together in a supportive and spiritually enriching environment.',
  },
  {
    title: 'Smart Progress Tracking',
    description:
      'Monitor your journey with detailed progress reports, quizzes, and milestones to celebrate every achievement.',
  },
  {
    title: 'Certificate of Completion',
    description:
      'Earn a recognized certificate upon completing the course, validating your Tajweed proficiency.',
  },
  {
    title: 'Female Instructors Available',
    description:
      'We offer dedicated female tutors for sisters and female students who prefer learning in a comfortable environment.',
  },
  {
    title: 'Free Trial Class',
    description:
      'Not sure yet? Attend a completely free trial session before committing, with no credit card required.',
  },
]

// ─── Why Take This Course — icon-free (backend-ready) ────────────────────────
export const COURSE_WHY = {
  eyebrow: 'WHY TAKE THIS COURSE',
  title: 'What Makes This Learning Experience ',
  highlight: 'Different',
  description:
    'We combine authentic Islamic scholarship with personalized teaching and modern learning tools to help you learn with confidence, consistency, and a deeper connection to the Quran.',
  reasons: [
    {
      title: 'Authentic & Reliable Learning',
      description:
        'Learn through carefully structured lessons based on authentic Quranic and Islamic sources, delivered by qualified teachers who prioritize accuracy and proper understanding.',
    },
    {
      title: 'Personalized Attention',
      description:
        'Every learner has different needs. Our one-on-one sessions allow teachers to adapt the lesson to your level, pace, goals, and areas for improvement.',
    },
    {
      title: 'Engaging Learning Methods',
      description:
        'We combine traditional Quranic teaching with modern educational techniques, interactive practice, and practical exercises to make learning clear and enjoyable.',
    },
    {
      title: 'A Meaningful Learning Environment',
      description:
        'Learning the Quran is more than gaining knowledge. It is a journey of faith and personal growth, supported by a respectful, encouraging, and spiritually uplifting environment.',
    },
  ],
}

// ─── What You Will Learn — icon-free (backend-ready) ─────────────────────────
export const COURSE_WHAT_YOU_LEARN = {
  eyebrow: 'WHAT YOU WILL LEARN',
  title: 'Course Curriculum',
  highlight: 'Highlights',
  description:
    'A comprehensive breakdown of every topic covered in this course, from foundational rules to advanced recitation techniques.',
  modules: [
    {
      title: 'Arabic Alphabet Mastery',
      description:
        'Perfect pronunciation of all 29 Arabic letters with correct Makharij (articulation points) and Sifaat (characteristics).',
    },
    {
      title: 'Noon & Meem Saakin Rules',
      description:
        'Master Izhaar, Idghaam, Iqlaab, and Ikhfaa — the four rules governing Noon Saakin and Tanween recitation.',
    },
    {
      title: 'Madd (Elongation) Rules',
      description:
        "Understand all types of Madd — Tabee'i, Muttasil, Munfasil, Lazim — and how to apply the correct elongation lengths.",
    },
    {
      title: 'Waqf & Ibtidaa',
      description:
        'Learn the rules of stopping (Waqf) and beginning (Ibtidaa) recitation correctly with proper breath management.',
    },
    {
      title: 'Lahn (Common Mistakes)',
      description:
        'Identify and correct the most common recitation errors (Lahn Jali & Lahn Khafi) that affect the meaning of Quranic verses.',
    },
    {
      title: 'Practical Recitation Sessions',
      description:
        'Apply every rule in live recitation practice sessions with real-time feedback from your certified instructor.',
    },
    {
      title: 'Group Recitation Circles',
      description:
        'Participate in weekly group Halaqat sessions — a tradition of the Prophet ﷺ — for mutual learning and spiritual motivation.',
    },
    {
      title: 'Assessments & Certification',
      description:
        'Regular quizzes, mid-term assessments, and a final recitation evaluation leading to your certificate of completion.',
    },
  ],
}
